import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import wlLogoWhite from "../../img/wl-tagline-logo-white.png";
import propertyPhoto from "../../img/module-housing-rent.jpg";
import "./ViewingApplication.css";
import "./ViewingApplicationListings.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const formatEuro = (value) => `€${Math.round(value).toLocaleString("nl-NL")}`;

// "2026-09-08" -> "08/09/2026".
const formatDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

// The form route is keyed on the Welocate reference; fall back to whatever
// identifier the listing carries.
const listingRef = (listing) =>
  listing.wh_ref_id ?? listing.whRefId ?? listing.ref ?? listing.id ?? null;

// The list endpoint may return a bare array or wrap it in an object.
const unwrapListings = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.listings)) return data.listings;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const listingName = (listing) =>
  [listing.street, listing.house_number, listing.floor]
    .filter(Boolean)
    .join(" ") || listingRef(listing) || "Property";

const listingMeta = (listing) =>
  [
    [listing.postcode, listing.city].filter(Boolean).join(" "),
    listing.area != null ? `${listing.area} m²` : null,
    listing.bedrooms != null
      ? `${listing.bedrooms} bedroom${listing.bedrooms === 1 ? "" : "s"}`
      : null,
    listing.is_furnished == null
      ? null
      : listing.is_furnished
      ? "Furnished"
      : "Unfurnished",
  ]
    .filter(Boolean)
    .join(" · ");

const ViewingApplicationListings = () => {
  // Any query parameters on this page (applicationId, clientId, ...) are
  // carried over to the form so the link keeps working.
  const { search } = useLocation();
  const [listings, setListings] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    const loadListings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/welocate/listings/active`);
        if (!response.ok) {
          throw new Error(`Failed to load listings (${response.status})`);
        }
        const data = await response.json();
        if (!cancelled) {
          setListings(unwrapListings(data).filter(listingRef));
          setStatus("ready");
        }
      } catch (error) {
        console.error("Could not load listings", error);
        if (!cancelled) setStatus("error");
      }
    };
    loadListings();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="va-page">
      <header className="va-topbar">
        <img className="va-logo" src={wlLogoWhite} alt="Welocate" />
        <h1 className="va-topbar-title">Application for renting property</h1>
        <div className="va-topbar-right">
          <span className="va-secure">
            <LockOutlinedIcon />
            Secure form
          </span>
        </div>
      </header>

      <main className="va-main">
        <div className="va-listings-intro">
          <h2 className="va-listings-title">Choose the home you want to apply for</h2>
          <p className="va-listings-sub">
            Select a property below to open its application form.
          </p>
        </div>

        {status === "loading" && (
          <div className="va-listings-grid" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="va-card va-listing-card va-listing-card--skeleton">
                <div className="va-listing-photo va-skeleton" />
                <div className="va-listing-body">
                  <div className="va-skeleton va-skeleton--line" />
                  <div className="va-skeleton va-skeleton--line va-skeleton--short" />
                </div>
              </div>
            ))}
          </div>
        )}

        {status === "error" && (
          <section className="va-card va-not-found">
            <h2 className="va-not-found-title">Listings unavailable</h2>
            <p className="va-not-found-text">
              We could not load the available homes right now. Please try again
              in a moment, or use the link you received from Welocate.
            </p>
          </section>
        )}

        {status === "ready" && listings.length === 0 && (
          <section className="va-card va-not-found">
            <h2 className="va-not-found-title">No homes available</h2>
            <p className="va-not-found-text">
              There are no properties open for applications at the moment.
              Please check back later or use the link you received from
              Welocate.
            </p>
          </section>
        )}

        {status === "ready" && listings.length > 0 && (
          <div className="va-listings-grid">
            {listings.map((listing) => {
              const ref = listingRef(listing);
              const name = listingName(listing);
              return (
                <Link
                  key={ref}
                  to={`/viewing_application/${encodeURIComponent(ref)}${search}`}
                  className="va-card va-listing-card"
                >
                  <img
                    className="va-listing-photo"
                    src={listing.image_url || propertyPhoto}
                    alt={name}
                    loading="lazy"
                  />
                  <div className="va-listing-body">
                    <h3 className="va-listing-name">{name}</h3>
                    <p className="va-listing-meta">{listingMeta(listing)}</p>
                    <dl className="va-listing-facts">
                      <div className="va-fact">
                        <dt>Asking rent</dt>
                        <dd>
                          {listing.monthly_rent != null
                            ? formatEuro(listing.monthly_rent)
                            : "–"}
                          {listing.monthly_rent != null && (
                            <span className="va-permonth">/mo</span>
                          )}
                        </dd>
                      </div>
                      <div className="va-fact">
                        <dt>Free from</dt>
                        <dd>
                          {listing.available_date
                            ? formatDate(listing.available_date)
                            : "–"}
                        </dd>
                      </div>
                    </dl>
                    <span className="va-listing-cta">
                      Apply for this home
                      <ChevronRightIcon />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ViewingApplicationListings;
